import React, { useState } from 'react';
import { CreditCardIcon, PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, accountNav } from '../../../components/layout/SubNav';
import { Card, KeyValue } from '../../../components/ui/Panels';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { customer } from '../../../data/customer';
import { useAppData } from '../../../contexts/AppDataContext';
import { useLocale } from '../../../contexts/LocaleContext';
import { currency, dateWithYear } from '../../../utils/format';
export function Payments() {
  const [methods, setMethods] = useState(customer.paymentMethods);
  const {
    bookings,
    quoteById
  } = useAppData();
  const {
    locale,
    currencyCode
  } = useLocale();
  const history = bookings.filter(b => b.customerId === customer.id && (b.status === 'completed' || b.status === 'confirmed')).sort((a, b) => b.date.localeCompare(a.date));
  const makeDefault = (id: string) => setMethods(methods.map(m => ({
    ...m,
    isDefault: m.id === id
  })));
  return <AppShell title="Payments" subtitle="Deposits, balances and receipts" data-id="element-1608" data-mp="pbmcdm6u5zul">
      <SubNav items={accountNav} data-id="element-1609" data-mp="am80o7fsf6c1" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-1610" data-mp="al6nnxf7o9ro">
        <div className="space-y-6" data-id="element-1611" data-mp="fl1clvffepa3">
          <Card className="p-5" data-id="element-1612" data-mp="fjzs70cisqfp">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1613" data-mp="1mbhglc62zod">Payment methods</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1614" data-mp="6m7a5ofez4om">
              A deposit is authorised when you accept a quote. The balance is charged after service.
            </p>
            <ul className="mt-4 space-y-2.5" data-id="element-1615" data-mp="3nw5nd3bk8lt4">
              {methods.map(method => <li key={method.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-hairline px-3.5 py-3" data-id="element-1616" data-mp="3suyso8gp16i7">
                  <span className="flex h-9 w-12 items-center justify-center rounded bg-oat text-ink-2" aria-hidden="true" data-id="element-1617" data-mp="1r0zk3uuyh397">
                    <CreditCardIcon className="h-4 w-4" data-id="element-1618" data-mp="1phzikx4a76kc" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-1619" data-mp="1kjxz4b2y158c">
                    <span className="block text-sm font-medium text-ink" data-id="element-1620" data-mp="1flebm68pbldv">
                      {method.brand} ···· {method.last4}
                    </span>
                    <span className="block text-[12px] text-ink-3" data-id="element-1621" data-mp="1bmxf0gmwptap">
                      {method.holder} · expires {method.expiry}
                    </span>
                  </span>
                  {method.isDefault ? <Badge tone="primary" data-id="element-1622" data-mp="1yksan2lafmb8">Default</Badge> : <Button type="button" variant="ghost" size="sm" onClick={() => makeDefault(method.id)} data-id="element-1623" data-mp="1yinyxyh5dj0e">
                      Make default
                    </Button>}
                </li>)}
            </ul>
            <Button type="button" variant="outline" size="sm" className="mt-4" data-id="element-1624" data-mp="2m9jv17smz0dg">
              <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1625" data-mp="8si4nccgrmuu" />
              Add a card or PromptPay
            </Button>
          </Card>

          <Card className="p-5" data-id="element-1626" data-mp="drlav046jz4e">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1627" data-mp="hsn1wtny14va">Receipts</h2>
            <p className="mt-1 text-sm text-ink-2" data-id="element-1628" data-mp="hurgq6ndroac">Every charge, split into what was deposit and what was balance.</p>
            <ul className="mt-4 divide-y divide-hairline" data-id="element-1629" data-mp="rqufd2j0x4sj">
              {history.map(booking => {
              const quote = quoteById(booking.quoteId);
              return <li key={booking.id} className="py-3" data-id="element-1630" data-mp="wnkxdvee2agl">
                    <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-1631" data-mp="11hxfo244pfqr">
                      <span className="text-sm font-medium text-ink" data-id="element-1632" data-mp="15h6azbethlof">
                        {booking.menuName ?? booking.occasion}
                        <span className="tnum ml-2 text-[12px] font-normal text-ink-3" data-id="element-1633" data-mp="1af7svt0ibeyb">{booking.ref}</span>
                      </span>
                      <span className="tnum text-sm font-semibold text-ink" data-id="element-1634" data-mp="11teytif8ujtj">
                        {currency(booking.total ?? 0, locale, currencyCode)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[12px] text-ink-3" data-id="element-1635" data-mp="1bk168pct4415">
                      {dateWithYear(booking.date, locale)}
                      {quote ? ` · deposit ${currency(quote.depositDue, locale, currencyCode)} · balance ${currency(quote.balanceDue, locale, currencyCode)}` : ''}
                    </p>
                  </li>;
            })}
            </ul>
          </Card>
        </div>

        <aside className="space-y-4" data-id="element-1636" data-mp="2485ffkul6z8l">
          <Card className="p-5" data-id="element-1637" data-mp="24743le5qy8pt">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1638" data-mp="246c8s5jz4f0l">How charges work</h2>
            <dl className="mt-3" data-id="element-1639" data-mp="1zbeyyweve1mn">
              <KeyValue label="On accepting a quote" value="Deposit authorised" data-id="element-1640" data-mp="26s1zmmor1erw" />
              <KeyValue label="After service" value="Balance charged" data-id="element-1641" data-mp="2breote33exxn" />
              <KeyValue label="At-cost groceries" value="Billed with receipts" data-id="element-1642" data-mp="2bplhdwkno9ol" />
              <KeyValue label="Service fee" value="10%, shown on the quote" data-id="element-1643" data-mp="1xthqac52whgc" />
              <KeyValue label="VAT" value="7%" data-id="element-1644" data-mp="21ujp4rv32rhs" />
            </dl>
          </Card>

          <Card className="p-5" data-id="element-1645" data-mp="2pikx9zoco5wo">
            <div className="flex items-start gap-2.5" data-id="element-1646" data-mp="2pjmjn0atgetp">
              <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-1647" data-mp="2uipclmdiuulb" />
              <p className="text-[13px] leading-relaxed text-ink-2" data-id="element-1648" data-mp="2grasfc7vonco">
                Money is held until service completes. If a chef cancels on you, the deposit is returned in full regardless
                of their cancellation tier.
              </p>
            </div>
          </Card>
        </aside>
      </div>
    </AppShell>;
}